import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { CallbackID, Geolocation, Position } from '@capacitor/geolocation';
import { CapgoCompass, CompassAccuracy } from '@capgo/capacitor-compass';
import { AlertController, AlertOptions, Platform, PopoverController } from '@ionic/angular';
import { azimuthToBearing, destination } from '@turf/turf';

import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { model } from 'geomagnetism';
import { has, isNull } from 'lodash-es';
import { asyncScheduler, BehaviorSubject, combineLatest, tap, throttleTime } from 'rxjs';

import { ProjectionData } from '../../typings/compass';

interface WebViewDeviceOrientationEvent {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
  webkitCompassAccuracy?: number | null;
  webkitCompassHeading?: number | null;
}

interface IOSWebViewDeviceOrientationEvent extends WebViewDeviceOrientationEvent {
  requestPermission: () => Promise<NotificationPermission>;
}

@Injectable({
  providedIn: 'root'
})
export class CompassService {
  private platform = inject(Platform);
  private popoverController = inject(PopoverController);
  private router = inject(Router);
  private alertController = inject(AlertController);

  readonly minCoordsAccuracyInMeters = 20;

  private headingListener: { remove: () => Promise<void> } | null = null;
  private accuracyListener: { remove: () => Promise<void> } | null = null;

  heading$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  accuracy$: BehaviorSubject<CompassAccuracy> = new BehaviorSubject<CompassAccuracy>(CompassAccuracy.UNKNOWN);
  position$: BehaviorSubject<Position | null> = new BehaviorSubject<Position | null>(null);

  projectedLocation$: BehaviorSubject<ProjectionData | null> = new BehaviorSubject<ProjectionData | null>(null);

  private shouldUpdateDeclination = true;
  private declination = 0;

  private geolocationCallBackId: CallbackID | null = null;
  constructor() {
    combineLatest([this.heading$, this.accuracy$, this.position$])
      .pipe(
        throttleTime(100, asyncScheduler, { leading: true, trailing: true }),
        tap(([heading, accuracy, positionData]) => {
          let projectedLocation: google.maps.LatLngLiteral | null = null;
          let isAccuracyReady = false;
          let compassAccuracy: CompassAccuracy = CompassAccuracy.UNKNOWN;
          let geolocationAccuracy = null;
          let geolocationPosition: google.maps.LatLngLiteral | null = null;
          let normalizedHeading = null;
          if (!isNull(positionData) && !isNull(heading) && !isNull(accuracy)) {
            normalizedHeading = heading + this.declination;
            if (normalizedHeading < 0) {
              normalizedHeading += 360;
            } else if (normalizedHeading > 360) {
              normalizedHeading -= 360;
            }
            geolocationPosition = {
              lat: positionData.coords.latitude,
              lng: positionData.coords.longitude
            };
            geolocationAccuracy = positionData.coords.accuracy;
            const dest = destination(
              [positionData.coords.longitude, positionData.coords.latitude],
              this.platform.is('ios') ? 0.01524 : 0.021336,
              azimuthToBearing(normalizedHeading)
            );
            projectedLocation = { lat: dest.geometry.coordinates[1], lng: dest.geometry.coordinates[0] };
            isAccuracyReady =
              positionData.coords.accuracy <= this.minCoordsAccuracyInMeters && accuracy >= CompassAccuracy.LOW;
            compassAccuracy = accuracy;
          }
          this.projectedLocation$.next({
            compassAccuracy,
            compassHeading: normalizedHeading,
            geolocationAccuracy,
            geolocationPosition,
            isAccuracyReady,
            projectedLocation
          });
        })
      )
      .subscribe();
  }

  async start() {
    this.headingListener = await CapgoCompass.addListener('headingChange', (data) => {
      this.heading$.next(data.value);
    });
    await CapgoCompass.startListening();
    if (this.platform.is('android')) {
      this.accuracyListener = await CapgoCompass.addListener('accuracyChange', (data) => {
        this.accuracy$.next(data.accuracy);
      });
      await CapgoCompass.watchAccuracy();
    } else {
      this.accuracy$.next(CompassAccuracy.HIGH);
    }
    this.shouldUpdateDeclination = true;
    this.geolocationCallBackId = await Geolocation.watchPosition(
      {
        enableHighAccuracy: true,
        maximumAge: 1000
      },
      (position) => {
        this.position$.next(position);
        if (!isNull(position) && this.shouldUpdateDeclination && this.platform.is('android')) {
          this.shouldUpdateDeclination = false;
          const currentModel = model();
          const info = currentModel.point([position.coords.longitude, position.coords.latitude]);
          this.declination = info.decl;
        }
      }
    );
    return this.projectedLocation$.asObservable();
  }

  async stop() {
    await CapgoCompass.stopListening();
    this.headingListener?.remove();
    this.accuracyListener?.remove();
    if (this.geolocationCallBackId) {
      await Geolocation.clearWatch({ id: this.geolocationCallBackId });
    }
    this.heading$.next(null);
    this.position$.next(null);
    this.projectedLocation$.next(null);
  }

  async navigateToCameraView() {
    try {
      await this.requestGeolocationPermissions();
      await this.requestDeviceOrientationPermissions();
      await this.requestCameraPermissions();
      await this.popoverController.dismiss();
      await this.router.navigate(['/property-search', 'camera']);
    } catch (error) {
      let message = 'An unknown error has occurred.';
      if (has(error, 'message')) {
        message = error.message;
      }
      const alertOptions: AlertOptions = {
        backdropDismiss: false,
        buttons: [
          {
            role: 'cancel',
            text: 'Ok'
          }
        ],
        header: 'Feature Unavailable',
        message: message
      };
      if (Capacitor.isNativePlatform() && has(error, 'showSettings') && error.showSettings) {
        alertOptions.buttons?.push({
          handler: () =>
            NativeSettings.open({
              optionAndroid: AndroidSettings.Location,
              optionIOS: IOSSettings.App
            }),
          role: 'settings',
          text: 'Settings'
        });
      }
      const alert = await this.alertController.create(alertOptions);
      await alert.present();
    }
  }

  async requestGeolocationPermissions() {
    return new Promise<void>((resolve, reject) => {
      Geolocation.checkPermissions().then(
        ({ location }) => {
          if (location === 'granted') {
            resolve();
          } else {
            Geolocation.requestPermissions({ permissions: ['location'] }).then(
              ({ location }) => {
                if (location === 'granted') {
                  resolve();
                } else {
                  reject({
                    message:
                      'In order to use this feature you need to grant all the required permissions. Location permissions not granted, please use your device settings to grant access.',
                    showSettings: true
                  });
                }
              },
              () => {
                reject({
                  message: 'There was an error while requesting location permissions.',
                  showSettings: false
                });
              }
            );
          }
        },
        () => {
          reject({ message: 'There was an error while checking location permissions.', showSettings: false });
        }
      );
    });
  }

  async requestDeviceOrientationPermissions() {
    return new Promise<void>((resolve, reject) => {
      if (this.platform.is('ios')) {
        (DeviceOrientationEvent as unknown as IOSWebViewDeviceOrientationEvent).requestPermission().then(
          (permission) => {
            const granted = permission === 'granted';
            if (granted) {
              resolve();
            } else {
              reject({
                message:
                  'In order to use this feature you need to grant all the required permissions. Orientation permissions are not granted, please use your device settings to grant access.',
                showSettings: true
              });
            }
          },
          () => {
            reject({
              message: 'There was an error while requesting orientation permissions.',
              showSettings: false
            });
          }
        );
      } else {
        resolve();
      }
    });
  }

  async requestCameraPermissions() {
    return new Promise<void>((resolve, reject) => {
      Camera.checkPermissions().then(
        ({ camera }) => {
          if (camera === 'granted') {
            resolve();
          } else {
            Camera.requestPermissions({ permissions: ['camera'] }).then(
              ({ camera }) => {
                if (camera === 'granted') {
                  resolve();
                } else {
                  reject({
                    message:
                      'In order to use this feature you need to grant all the required permissions. Camera permissions not granted, please use your device settings to grant access.',
                    showSettings: true
                  });
                }
              },
              () => {
                reject({
                  message: 'There was an error while requesting camera permissions.',
                  showSettings: false
                });
              }
            );
          }
        },
        () => {
          reject({ message: 'There was an error while checking camera permissions.', showSettings: false });
        }
      );
    });
  }
}
